import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import clsx from 'clsx';
import { useGetAccountsStore, useGetStatusStore } from '@lib/store';
import { RoutePaths } from '@shared/utils';
import { RecipientTypeEnum } from '@shared/api/rest/models';
import type { GetRequisitesResponse } from '@shared/api/rest/payments';
import { TopUpMyAccountModal } from '@widgets/AccountsSlider/widgets';
import { AddNewRecipientModal, ExternalPaymentsModal, SelectRecipientModal, TransferBetweenMyAccountModal, TransferToCardModal } from './widgets';
import { ButtonV2 } from '../button';

interface PaymentsProps {
  className?: string;
  paymentsTab?: boolean;
}

export const Payments = (props: PaymentsProps) => {
  const getAccountsStore = useGetAccountsStore();
  const statusStore = useGetStatusStore();

  useEffect(() => {
    getAccountsStore.get();
  }, []);

  const [recipient, setRecipient] = useState<GetRequisitesResponse>();
  const [typeRecipientChange, setTypeRecipientChange] = useState<RecipientTypeEnum>(RecipientTypeEnum.Add);
  const [isVisibleTransferToCardModal, setIsVisibleTransferToCardModal] = useState<boolean>(false);
  const [isVisibleExternalPaymentsModal, setIsVisibleExternalPaymentsModal] = useState<boolean>(false);
  const [isVisibleSelectAccountModal, setIsVisibleSelectAccountModal] = useState<boolean>(false);
  const [isVisibleAddNewRecipientModal, setIsVisibleAddNewRecipientModal] = useState<boolean>(false);
  const [isVisibleTransferBetweenMyAccounts, setIsVisibleTransferBetweenMyAccounts] = useState<boolean>(false);
  const [isVisibleTopMyAccount, setIsVisibleTopMyAccount] = useState<boolean>(false);

  return (
    <>
      {props.paymentsTab ? (
        <div className={clsx('border-y border-y-[#97815e33] max-md:-mx-5', props.className)}>
          <div className='flex h-[70px] w-full items-center justify-between overflow-x-auto whitespace-nowrap max-md:px-5'>
            <div className='mr-[0.5%] flex h-[70px] cursor-pointer items-center justify-center border-b border-b-[currentColor] text-center text-sm text-[#fcfcfc] uppercase max-md:mx-5 max-md:ml-0'>
              Overview
            </div>
            <div
              className='mx-[0.5%] flex h-[70px] cursor-pointer items-center justify-center text-center text-sm text-[#fcfcfc] uppercase opacity-50 max-md:mx-5'
              onClick={() => setIsVisibleTransferToCardModal(true)}
            >
              Transfer to Card
            </div>
            <div
              className='mx-[0.5%] flex h-[70px] cursor-pointer items-center justify-center text-center text-sm text-[#fcfcfc] uppercase opacity-50 max-md:mx-5'
              onClick={() => setIsVisibleTopMyAccount(true)}
            >
              Top up my accounts
            </div>
            <div
              className='mx-[0.5%] flex h-[70px] cursor-pointer items-center justify-center text-center text-sm text-[#fcfcfc] uppercase opacity-50 max-md:mx-5'
              onClick={() => setIsVisibleTransferBetweenMyAccounts(true)}
            >
              Internal transfer
            </div>
            <div
              className={clsx(
                'ml-[0.5%] flex h-[70px] cursor-pointer items-center justify-center text-center text-sm text-[#fcfcfc] uppercase opacity-50 max-md:ml-5',
                statusStore.data?.OperationalStatus === 'NonOperational' ? 'pointer-events-none !text-[#f2f2f24d]' : '',
              )}
              onClick={() => setIsVisibleExternalPaymentsModal(true)}
            >
              External transfer
            </div>
          </div>
        </div>
      ) : (
        <div className={clsx(props.className)}>
          <div className='mb-6.5 flex h-[20px] items-center justify-between pl-3 select-none max-md:mb-5 max-md:h-[15px] max-md:p-0 max-md:pl-2.5'>
            <div className='relative text-xl font-bold text-[#f2f2f2] uppercase before:absolute before:top-0 before:left-[-10px] before:font-semibold before:text-[#a73f33] before:content-["/"] max-md:text-sm'>
              Payments
            </div>
          </div>
          <div className='grid grid-cols-2 items-center justify-center gap-2 max-md:flex max-md:flex-col max-md:flex-wrap'>
            <ButtonV2 className='!h-[65px]' fullWidth onClick={() => setIsVisibleTransferToCardModal(true)}>
              Transfer to card
            </ButtonV2>
            <ButtonV2 className='!h-[65px]' fullWidth onClick={() => setIsVisibleTransferBetweenMyAccounts(true)}>
              Transfer between my accounts
            </ButtonV2>
            <ButtonV2
              disabled={statusStore.data?.OperationalStatus === 'NonOperational'}
              className='!h-[65px]'
              fullWidth
              onClick={() => setIsVisibleExternalPaymentsModal(true)}
            >
              External payments
            </ButtonV2>
            <ButtonV2 className='!h-[65px]' fullWidth>
              <Link to={RoutePaths.payments.onNavigate()}>Payments overview</Link>
            </ButtonV2>
          </div>
        </div>
      )}
      <TransferToCardModal isOpen={isVisibleTransferToCardModal} onClose={() => setIsVisibleTransferToCardModal(false)} />
      {props.paymentsTab && (
        <TopUpMyAccountModal isOpen={isVisibleTopMyAccount} onClose={() => setIsVisibleTopMyAccount(false)} account={getAccountsStore.data?.[0]} />
      )}
      <TransferBetweenMyAccountModal isOpen={isVisibleTransferBetweenMyAccounts} onClose={() => setIsVisibleTransferBetweenMyAccounts(false)} />
      <ExternalPaymentsModal
        isOpen={isVisibleExternalPaymentsModal}
        onClose={() => setIsVisibleExternalPaymentsModal(false)}
        setIsOpenSelectAccountModal={setIsVisibleSelectAccountModal}
        selectedRecipient={recipient}
      />
      <SelectRecipientModal
        isOpen={isVisibleSelectAccountModal}
        onClose={setIsVisibleSelectAccountModal}
        setIsOpenAddNewRecipientModal={setIsVisibleAddNewRecipientModal}
        setIsVisibleExternalPaymentsModal={setIsVisibleExternalPaymentsModal}
        setRecipient={setRecipient}
        selectedRecipient={recipient}
        setTypeRecipientChange={setTypeRecipientChange}
      />
      <AddNewRecipientModal
        isOpen={isVisibleAddNewRecipientModal}
        typeRecipientChange={typeRecipientChange}
        selectedRecipient={recipient}
        setIsVisibleSelectAccountModal={setIsVisibleSelectAccountModal}
        onClose={() => setIsVisibleAddNewRecipientModal(false)}
      />
    </>
  );
};
